import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Cookie, Settings2, ShieldCheck, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { api } from "@/lib/api";
import {
  readStoredConsent, writeStoredConsent, COOKIE_SETTINGS_EVENT, LEGAL_VERSION,
} from "@/lib/legalVersion";

// Phase P.18 — GDPR — Cookie consent banner + granular Customize modal.
// Behaviour:
//  - Mounts globally in App.js so every public route shows the banner.
//  - Reads existing stored consent; only shows banner if none exists OR the
//    stored consent version < current LEGAL_VERSION (material policy change).
//  - Three primary actions: Accept all / Reject non-essential / Customize.
//  - Customize opens a modal with Functional & Analytics toggles. Essential
//    is always-on and locked (strictly-necessary — no consent required per
//    Irish ePrivacy Regs Reg 5(5)).
//  - Choice is persisted to localStorage. If a JWT is present, also POSTed
//    to `POST /api/consent/cookie` for the audit trail.
//  - Footer "Cookie settings" link fires a custom event that re-opens the
//    banner (so users can revisit their choice at any time).

const DEFAULT_CATEGORIES = {
  essential: true,
  functional: true,
  analytics: false,
};

async function persistConsentServer(choice, categories) {
  // Only attempt server-side record if we have an auth token — otherwise the
  // request would 401 and we'd surface a scary error on marketing pages.
  const token = typeof window !== "undefined" ? localStorage.getItem("jb_token") : null;
  if (!token) return;
  try {
    await api.post("/consent/cookie", {
      choice,
      categories,
      version: LEGAL_VERSION,
    });
  } catch (_) {
    // Non-blocking — the local record is the source of truth for UX gating.
  }
}

export default function CookieConsent() {
  const [visible, setVisible] = useState(false);
  const [customizeOpen, setCustomizeOpen] = useState(false);
  const [categories, setCategories] = useState(DEFAULT_CATEGORIES);

  // Initial mount — decide whether to show the banner. Also subscribe to the
  // "open cookie settings" event so the footer link + /cookies page button
  // can re-open the banner on demand.
  useEffect(() => {
    const stored = readStoredConsent();
    if (!stored) {
      setVisible(true);
    } else if (stored.categories) {
      setCategories({ ...DEFAULT_CATEGORIES, ...stored.categories });
    }
    const onOpen = () => {
      const s = readStoredConsent();
      if (s?.categories) setCategories({ ...DEFAULT_CATEGORIES, ...s.categories });
      setVisible(true);
    };
    window.addEventListener(COOKIE_SETTINGS_EVENT, onOpen);
    return () => window.removeEventListener(COOKIE_SETTINGS_EVENT, onOpen);
  }, []);

  if (!visible) return null;

  const acceptAll = () => {
    const cats = { essential: true, functional: true, analytics: true };
    writeStoredConsent({ choice: "accept_all", categories: cats });
    persistConsentServer("accept_all", cats);
    setCategories(cats);
    setCustomizeOpen(false);
    setVisible(false);
  };

  const rejectNonEssential = () => {
    const cats = { essential: true, functional: false, analytics: false };
    writeStoredConsent({ choice: "reject_non_essential", categories: cats });
    persistConsentServer("reject_non_essential", cats);
    setCategories(cats);
    setCustomizeOpen(false);
    setVisible(false);
  };

  const saveCustom = () => {
    const cats = { ...categories, essential: true };
    writeStoredConsent({ choice: "custom", categories: cats });
    persistConsentServer("custom", cats);
    setCustomizeOpen(false);
    setVisible(false);
  };

  return (
    <>
      <div
        className="fixed inset-x-0 bottom-0 sm:inset-x-auto sm:right-4 sm:bottom-4 sm:left-auto z-[60] p-2 sm:p-0 pointer-events-none"
        role="dialog"
        aria-live="polite"
        aria-label="Cookie consent"
        data-testid="cookie-consent-banner"
      >
        <div className="pointer-events-auto sm:max-w-[360px] mx-auto sm:mx-0 bg-card text-card-foreground border border-border shadow-xl rounded-[8px] p-3">
          <div className="flex items-start gap-2">
            <div className="h-7 w-7 rounded-full bg-accent/10 grid place-items-center shrink-0">
              <Cookie className="h-3.5 w-3.5 text-accent" aria-hidden="true" />
            </div>
            <div className="flex-1 min-w-0">
              <h2 className="text-[13px] font-extrabold tracking-tight leading-tight">
                We use only the cookies we need.
              </h2>
              <p className="text-[11px] text-foreground/75 leading-snug mt-1">
                Strictly-necessary items keep you signed in. See our{" "}
                <Link to="/cookies" className="text-accent underline">Cookies</Link>{" "}·{" "}
                <Link to="/privacy" className="text-accent underline">Privacy</Link>.
              </p>
              <div className="mt-2.5 flex flex-wrap gap-1.5">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={rejectNonEssential}
                  className="h-7 px-2 text-[11px] gap-1"
                  data-testid="cookie-reject-non-essential"
                >
                  <ShieldCheck className="h-3 w-3" /> Reject
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCustomizeOpen(true)}
                  className="h-7 px-2 text-[11px] gap-1"
                  data-testid="cookie-customize"
                >
                  <Settings2 className="h-3 w-3" /> Customize
                </Button>
                <Button
                  size="sm"
                  onClick={acceptAll}
                  className="btn-cta h-7 px-2.5 text-[11px]"
                  data-testid="cookie-accept-all"
                >
                  Accept all
                </Button>
              </div>
            </div>
            <button
              type="button"
              onClick={rejectNonEssential}
              className="text-muted-foreground hover:text-foreground p-0.5 -mr-0.5 -mt-0.5"
              aria-label="Close and reject non-essential"
              data-testid="cookie-banner-close"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>
      </div>

      <Dialog open={customizeOpen} onOpenChange={setCustomizeOpen}>
        <DialogContent data-testid="cookie-customize-dialog">
          <DialogHeader>
            <DialogTitle>Cookie preferences</DialogTitle>
            <DialogDescription>
              Choose which categories you allow. You can change this at any time via the "Cookie
              settings" link in the footer.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <CategoryRow
              testid="cookie-cat-essential"
              title="Strictly necessary"
              locked
              on={true}
              onToggle={() => {}}
              desc="Sign-in, session, consent state. Cannot be disabled (Irish ePrivacy Reg 5(5))."
            />
            <CategoryRow
              testid="cookie-cat-functional"
              title="Functional"
              on={!!categories.functional}
              onToggle={(v) => setCategories((c) => ({ ...c, functional: v }))}
              desc="Remember UI preferences: calendar view, table density, dismissed hints."
            />
            <CategoryRow
              testid="cookie-cat-analytics"
              title="Analytics"
              on={!!categories.analytics}
              onToggle={(v) => setCategories((c) => ({ ...c, analytics: v }))}
              desc="None currently in use. This toggle records your future consent should we ever add analytics."
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={rejectNonEssential} data-testid="cookie-customize-reject">
              Reject non-essential
            </Button>
            <Button onClick={saveCustom} className="btn-cta" data-testid="cookie-customize-save">
              Save choices
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function CategoryRow({ title, desc, on, onToggle, locked = false, testid }) {
  return (
    <div className="flex items-start justify-between gap-3 p-3 border border-border rounded-md" data-testid={testid}>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <div className="font-semibold text-sm">{title}</div>
          {locked && (
            <span className="mono text-[9px] uppercase tracking-widest text-muted-foreground bg-secondary/60 border border-border rounded px-1.5 py-0.5">
              Always on
            </span>
          )}
        </div>
        <div className="text-xs text-muted-foreground mt-1 leading-relaxed">{desc}</div>
      </div>
      <Switch
        checked={on}
        onCheckedChange={locked ? undefined : onToggle}
        disabled={locked}
        aria-label={title}
        data-testid={`${testid}-switch`}
      />
    </div>
  );
}
